import React from 'react';
import HomeIcon from './HomeIcon';
import { getHomeCopy } from './homeCopy';

export type MarketingNavVals = {
  menuOpen01: string;
  toggleMenu: () => void;
  closeMenu: () => void;
  navBg: string;
  navTop: string;
  navRadius: string;
  navMax: string;
  navShadow: string;
  navBlur: string;
  czBg: string;
  czColor: string;
  enBg: string;
  enColor: string;
};

type Props = {
  v: MarketingNavVals;
  lang: 'cs' | 'en';
  onSetCz: () => void;
  onSetEn: () => void;
  /** Highlight nav item when on /kontakt, /o-nas, or /blog */
  active?: 'contact' | 'about' | 'blog' | null;
};

const linkStyle = (active: boolean): React.CSSProperties => ({
  fontSize: 15,
  fontWeight: active ? 600 : 500,
  color: active ? '#0065F4' : '#374151',
  transition: 'color 150ms ease-out',
});

const panelLinkStyle = (active: boolean): React.CSSProperties => ({
  fontSize: 16,
  fontWeight: active ? 600 : 500,
  color: active ? '#0065F4' : '#111827',
  padding: '13px 12px',
  borderRadius: 10,
});

/** Shared marketing header for `/`, `/kontakt`, `/o-nas`, and `/blog`. */
const MarketingHeader: React.FC<Props> = ({ v, lang, onSetCz, onSetEn, active = null }) => {
  const c = getHomeCopy(lang);
  const contactActive = active === 'contact';
  const aboutActive = active === 'about';
  const blogActive = active === 'blog';

  return (
    <header
      data-header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        padding: `${v.navTop} 24px 0`,
        transition: 'padding 260ms ease-out',
      }}
    >
      <div
        data-navbar
        style={{
          maxWidth: v.navMax,
          margin: '0 auto',
          padding: '0 24px',
          height: 72,
          display: 'flex',
          alignItems: 'center',
          gap: 40,
          borderRadius: v.navRadius,
          background: v.navBg,
          backdropFilter: v.navBlur,
          boxShadow: v.navShadow,
          transition:
            'background 220ms ease-out, backdrop-filter 220ms, border-radius 260ms ease-out, max-width 260ms ease-out, box-shadow 220ms ease-out',
        }}
      >
        <a href="/" style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
          <img src="/home/ompli-logo-blue.png" alt="Ompli" style={{ height: 26, width: 'auto', display: 'block' }} />
        </a>
        <nav data-nav-links style={{ display: 'flex', alignItems: 'center', gap: 28, flex: 1 }}>
          <a href="/#funkce" className="home-link-nav" style={linkStyle(false)}>
            {c.navFeatures}
          </a>
          <a href="/#role" className="home-link-nav" style={linkStyle(false)}>
            {c.navRoles}
          </a>
          <a href="/#cenik" className="home-link-nav" style={linkStyle(false)}>
            {c.navPricing}
          </a>
          <a
            href="/o-nas"
            className={aboutActive ? undefined : 'home-link-nav'}
            style={linkStyle(aboutActive)}
          >
            {c.navAbout}
          </a>
          <a
            href="/blog"
            className={blogActive ? undefined : 'home-link-nav'}
            style={linkStyle(blogActive)}
          >
            {c.navBlog}
          </a>
          <a
            href="/kontakt"
            className={contactActive ? undefined : 'home-link-nav'}
            style={linkStyle(contactActive)}
          >
            {c.navContact}
          </a>
        </nav>
        <div data-nav-actions style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div data-nav-lang style={{ display: 'flex', alignItems: 'center', background: '#EAF1FF', borderRadius: 999, padding: 3 }}>
            <button
              type="button"
              onClick={onSetCz}
              style={{
                border: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 600,
                padding: '5px 11px',
                borderRadius: 999,
                transition: 'all 150ms ease-out',
                background: v.czBg,
                color: v.czColor,
              }}
            >
              CZ
            </button>
            <button
              type="button"
              onClick={onSetEn}
              style={{
                border: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 600,
                padding: '5px 11px',
                borderRadius: 999,
                transition: 'all 150ms ease-out',
                background: v.enBg,
                color: v.enColor,
              }}
            >
              EN
            </button>
          </div>
          <a data-nav-login href="/login" style={{ fontSize: 15, fontWeight: 500, color: '#374151' }}>
            {c.navLogin}
          </a>
          <a
            data-nav-cta
            href="/register"
            className="home-btn-cta-nav"
            style={{
              background: '#0065F4',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1,
              padding: '10px 18px',
              borderRadius: 8,
              transition: 'background 150ms ease-out, transform 150ms ease-out',
            }}
          >
            {c.navCta}
          </a>
          <button
            data-nav-burger
            type="button"
            onClick={v.toggleMenu}
            aria-label="Menu"
            style={{
              display: 'none',
              width: 42,
              height: 42,
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #E5E7EB',
              background: '#fff',
              borderRadius: 11,
              cursor: 'pointer',
              flex: 'none',
              padding: 0,
            }}
          >
            <HomeIcon name="list" size={18} color="#111827" trigger="hover" target="button" />
          </button>
        </div>
      </div>
      <div
        data-nav-panel
        data-open={v.menuOpen01}
        style={{
          maxWidth: 1200,
          margin: '8px auto 0',
          flexDirection: 'column',
          gap: 2,
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'saturate(180%) blur(12px)',
          border: '1px solid #EDF0F5',
          borderRadius: 16,
          padding: 10,
          boxShadow: '0 14px 34px rgba(17,24,39,0.09)',
        }}
      >
        <a href="/#funkce" onClick={v.closeMenu} className="home-panel-link" style={panelLinkStyle(false)}>
          {c.navFeatures}
        </a>
        <a href="/#role" onClick={v.closeMenu} className="home-panel-link" style={panelLinkStyle(false)}>
          {c.navRoles}
        </a>
        <a href="/#cenik" onClick={v.closeMenu} className="home-panel-link" style={panelLinkStyle(false)}>
          {c.navPricing}
        </a>
        <a href="/o-nas" onClick={v.closeMenu} className="home-panel-link" style={panelLinkStyle(aboutActive)}>
          {c.navAbout}
        </a>
        <a href="/blog" onClick={v.closeMenu} className="home-panel-link" style={panelLinkStyle(blogActive)}>
          {c.navBlog}
        </a>
        <a href="/kontakt" onClick={v.closeMenu} className="home-panel-link" style={panelLinkStyle(contactActive)}>
          {c.navContact}
        </a>
        <a href="/login" onClick={v.closeMenu} className="home-panel-link" style={panelLinkStyle(false)}>
          {c.navLogin}
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 12px 4px', borderTop: '1px solid #EDF0F5', marginTop: 6 }}>
          <a
            href="/register"
            onClick={v.closeMenu}
            className="home-btn-primary"
            style={{
              flex: 1,
              textAlign: 'center',
              background: '#0065F4',
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              lineHeight: 1,
              padding: '14px 16px',
              borderRadius: 10,
            }}
          >
            {c.navCta}
          </a>
          <div style={{ display: 'flex', alignItems: 'center', background: '#EAF1FF', borderRadius: 999, padding: 3, flex: 'none' }}>
            <button
              type="button"
              onClick={onSetCz}
              style={{
                border: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 600,
                padding: '7px 12px',
                borderRadius: 999,
                background: v.czBg,
                color: v.czColor,
              }}
            >
              CZ
            </button>
            <button
              type="button"
              onClick={onSetEn}
              style={{
                border: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 600,
                padding: '7px 12px',
                borderRadius: 999,
                background: v.enBg,
                color: v.enColor,
              }}
            >
              EN
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default MarketingHeader;
